/**
 * El 4x1000: cuándo se cobra y cuánto. En UN solo sitio.
 *
 * ── POR QUÉ ESTE ARCHIVO EXISTE ─────────────────────────────────────────────
 *
 * Antes la regla vivía dentro de la hoja de registro, y las pantallas que se
 * hicieron después (préstamos, cuotas) movían plata sin preguntarle a nadie.
 * Resultado real: Esteban le pagó $200.000 a Carolay desde el Banco de
 * Bogotá y la app no descontó los $800 del impuesto. El saldo del banco
 * quedó $800 por encima de la verdad, y así con cada pago.
 *
 * Una regla de plata que está copiada en dos sitios termina diciendo dos
 * cosas distintas. Por eso ahora toda salida de plata pasa por aquí.
 *
 * ── LAS REGLAS ──────────────────────────────────────────────────────────────
 *
 * Salen de la hoja de cálculo que Esteban ya llevaba, más lo que dice la ley:
 *
 *   · Solo las SALIDAS pagan. Recibir plata no cuesta nada.
 *   · El efectivo no paga: el impuesto es sobre movimientos bancarios.
 *   · La cuenta marcada como exenta no paga (numeral 1, art. 879 E.T.).
 *   · Nequi, Daviplata y demás billeteras son depósitos de bajo monto: no
 *     pagan hasta 65 UVT al mes (numeral 25, art. 879 E.T.).
 *   · Los PSE SÍ pagan. La primera versión decía que no, por una fórmula
 *     de la hoja de Esteban. Su extracto real del Banco de Bogotá lo
 *     desmiente: el 15 de sept el banco cobró $5.313, que es exactamente el
 *     0,4% de TODO lo que salió ese día, con los tres PSE incluidos (sin
 *     ellos habrían sido $4.414). Manda el banco, no la fórmula.
 *   · Las cuentas de crédito (tarjeta, Addi) y las cuentas espejo ("Me deben",
 *     "Le debo a gente") no son cuentas bancarias: no pagan.
 *
 * Los topes mensuales NO se controlan todavía: si en un mes se pasa de los
 * 65 UVT en Nequi, el banco sí cobra y la app no lo sabría. Por eso cada
 * cálculo se muestra y se puede corregir a mano. Nunca decide en silencio.
 */

import { calcularGmf } from './dinero.js';
import { topeExentoGmf } from './parametros.js';

/** Tipos de cuenta que nunca pagan: no son cuentas bancarias de depósito. */
const NO_BANCARIAS = [
  'efectivo', 'tarjeta_credito', 'credito_rotativo', 'prestamo',
  'por_cobrar', 'por_pagar', 'inversion',
];

function pesos(n) {
  return '$ ' + new Intl.NumberFormat('es-CO').format(Math.round(n || 0));
}

/**
 * Cuánto 4x1000 cobra una salida de plata, y por qué.
 *
 * @param {object} p
 * @param {string} p.tipo          'egreso' | 'transferencia' | 'ingreso'
 * @param {number} p.monto         lo que salió, en pesos
 * @param {object} p.cuentaOrigen  la cuenta de donde sale (fila de `cuentas`)
 * @param {object} [p.entidad]     el banco de esa cuenta, si se conoce
 * @param {string} [p.metodoPago]
 * @returns {{ monto: number, razon: string|null }}
 *   `razon` explica por qué NO se cobra, para mostrarlo al lado del cero.
 */
export function gmfDe({ tipo, monto, cuentaOrigen, entidad = null, metodoPago = null }) {
  if (tipo === 'ingreso') return { monto: 0, razon: 'recibir plata no paga' };
  if (!monto || monto <= 0) return { monto: 0, razon: null };
  if (!cuentaOrigen) return { monto: 0, razon: null };

  if (cuentaOrigen.tipo === 'efectivo') {
    return { monto: 0, razon: 'el efectivo no paga' };
  }
  if (NO_BANCARIAS.includes(cuentaOrigen.tipo)) {
    return { monto: 0, razon: 'no es cuenta bancaria' };
  }
  if (cuentaOrigen.exenta_gmf) {
    return { monto: 0, razon: 'cuenta exenta' };
  }
  if (cuentaOrigen.tipo === 'billetera') {
    const tope = topeExentoGmf('bajo_monto');
    return {
      monto: 0,
      razon: tope
        ? `billetera: no paga hasta ${pesos(tope)} al mes`
        : 'billetera de bajo monto',
    };
  }
  if (entidad && entidad.cobra_gmf === false) {
    return { monto: 0, razon: `${entidad.nombre} no lo cobra` };
  }
  return { monto: calcularGmf(monto), razon: null };
}

/**
 * El movimiento del 4x1000, hijo del movimiento que lo causó.
 *
 * Va como fila aparte y no como columna del padre: así el saldo de la cuenta
 * sale bien sin lógica especial, y se puede borrar o corregir solo.
 * Hereda los enlaces del padre (préstamo, compra, abono) para que al borrar
 * el préstamo o el pago, el impuesto se vaya con ellos y no quede huérfano.
 */
export function movimientoGmf(padre, monto) {
  return {
    tipo: 'egreso',
    monto,
    fecha: padre.fecha,
    hora: padre.hora ?? null,   // va pegado a su movimiento, también en el orden del día
    concepto: '4x1000',
    cuenta_origen: padre.cuenta_origen,
    cuenta_destino: null,
    movimiento_padre_id: padre.id,
    origen_dato: 'automatico',
    metodo_pago: padre.metodo_pago ?? null,
    deuda_id: padre.deuda_id ?? null,
    compra_diferida_id: padre.compra_diferida_id ?? null,
    abono_id: padre.abono_id ?? null,
  };
}
